import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/client';
import { ApiError } from '../api/errors';
import { unwrap } from '../api/unwrap';
import { buildListQuerySerializer } from '../lib/list-query';

import type { ImportFormat } from './schemas';

export const tradeLinksKeys = {
  all: ['trade-links'] as const,
  list: (filter: { clientId?: string; includeDeleted: boolean }) =>
    ['trade-links', 'list', filter] as const,
  one: (id: string) => ['trade-links', 'one', id] as const,
};

export interface TradeLinkRecord {
  id: string;
  clientId: string;
  partnerSteamId: string;
  token: string;
  rawUrl: string;
  source: 'csv' | 'json' | 'text' | 'manual';
  importedBy: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface TradeLinkImportFailure {
  line: number;
  reason: string;
}

export interface TradeLinkImportResult {
  imported: TradeLinkRecord[];
  failed: TradeLinkImportFailure[];
}

// Phase 8.5-05 — server-side URL-driven list (ADR-014). Bracketed filter
// + signed sort + 1-indexed page; backend returns `{ items, page,
// pageSize, total }`.
export type TradeLinkSort = 'createdAt' | 'source';

export interface TradeLinksListFilter {
  clientId?: string | undefined;
  includeDeleted?: boolean | undefined;
}

export interface TradeLinksListInput {
  filter: TradeLinksListFilter;
  sort: { field: TradeLinkSort | null; dir: 'asc' | 'desc' };
  page: number;
  pageSize: number;
}

export interface TradeLinksListResult {
  items: TradeLinkRecord[];
  page: number;
  pageSize: number;
  total: number;
}

const formatTradeLinkSort = (sort: TradeLinksListInput['sort']): string => {
  const field = sort.field ?? 'createdAt';
  return sort.dir === 'desc' ? `-${field}` : field;
};

export const useTradeLinks = (input: TradeLinksListInput) =>
  useQuery({
    queryKey: ['trade-links', 'list', input] as const,
    queryFn: async (): Promise<TradeLinksListResult> => {
      const filter: { clientId?: string; includeDeleted?: 'true' } = {};
      if (input.filter.clientId) filter.clientId = input.filter.clientId;
      if (input.filter.includeDeleted) filter.includeDeleted = 'true';
      const result = await api.GET('/api/trade-links', {
        params: {
          query: {
            sort: formatTradeLinkSort(input.sort),
            page: String(input.page),
            pageSize: String(input.pageSize),
          },
        },
        querySerializer: buildListQuerySerializer(filter),
      });
      return unwrap(result) as TradeLinksListResult;
    },
  });

export const useTradeLink = (id: string | undefined) =>
  useQuery({
    queryKey: id ? tradeLinksKeys.one(id) : ['trade-links', 'one', 'pending'],
    enabled: Boolean(id),
    queryFn: async (): Promise<TradeLinkRecord> => {
      if (!id) throw new Error('trade-link id required');
      const result = await api.GET('/api/trade-links/{id}', { params: { path: { id } } });
      return unwrap(result).tradeLink;
    },
  });

const invalidateTradeLinks = (qc: ReturnType<typeof useQueryClient>): Promise<void> =>
  qc.invalidateQueries({ queryKey: tradeLinksKeys.all });

export const useDeleteTradeLink = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const result = await api.DELETE('/api/trade-links/{id}', { params: { path: { id } } });
      unwrap(result);
    },
    onSuccess: () => invalidateTradeLinks(qc),
  });
};

export interface ImportTradeLinksArgs {
  clientId: string;
  format: ImportFormat;
  file: File;
}

export const useImportTradeLinks = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      clientId,
      format,
      file,
    }: ImportTradeLinksArgs): Promise<TradeLinkImportResult> => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(
        `/clients/${encodeURIComponent(clientId)}/trade-links/import?format=${encodeURIComponent(format)}`,
        {
          method: 'POST',
          credentials: 'include',
          body: formData,
        },
      );
      if (!response.ok) {
        const text = await response.text();
        let code = text;
        try {
          const parsed = JSON.parse(text) as { error?: string };
          code = parsed.error ?? text;
        } catch {
          // raw text fallthrough
        }
        throw new ApiError(response.status, code || 'import_failed');
      }
      return (await response.json()) as TradeLinkImportResult;
    },
    onSuccess: () => invalidateTradeLinks(qc),
  });
};
